<script>
export default {
  title: 'Project 5: Cat and Mouse GUI',
  starterId: null
}
</script>

<template>
  <section>
    <article>
      <h2>Background</h2>

      <figure>
        <img class="img-responsive center-block" src="./images/cat-and-mouse.png" width="560">
        <figcaption>Figure 1. The Cat and Mouse problem for which you'll write a GUI</figcaption>
      </figure>

      <p>At this point you are proud authors of a delightful cat and mouse simulation.</p>
    </article>

    <article>
      <h2>Your Task</h2>

      <p>
        You are asked to author a graphical user interface (GUI) for the <b>Cat and Mouse simulation you
        wrote for project 3</b>. Since you are not required to know about <dfn>threads</dfn> in CS9G,
        your GUI will probably not respond while the simulation is running -- this is perfectly acceptable.
        However, to indicate to the user that the GUI won't be listening, all of the buttons should be
        disabled until the simulation stops. If you'd like to learn how make your GUI respond to clicks
        <em>while</em> the simulation is running, read chapter 15 that covers threads.
      </p>

      <p>
        The interface will support the following buttons to control the simulation. Again, these buttons
        will probably only be responsive if the simulation is stopped, and that's fine.
      </p>

      <ul>
        <li>
          <strong>reset</strong> - Clicking this resets all variables (<code>catRadius</code>, <code>catAngle</code>,
          and <code>mouseAngle</code>) to the original values and resets the "time" to 0.
        </li>
        <li>
          <strong>step</strong> - Clicking this advances time one step (one minute in this case) in the
          simulation. This is enabled until the simulation is finished, at which point it should be
          disabled.</li>
        <li>
          <strong>run</strong> - Clicking this runs the simulation from its current 'time' to completion
          (the mouse is caught or 30 "minutes" have elapsed). The GUI does not quit after it's done simulating,
          because the user may wish to 'reset' and see it again. After a simulation has finished, only the
          reset and quit buttons should be enabled.
        </li>
        <li>
          <strong>quit</strong> - Clicking this quits the simulation.
        </li>
      </ul>

      <p>and the following other GUI elements:</p>

      <ul>
        <li>
          A time label (how many steps [minutes, in this case] are we in the simulation) that reads:
          <strong>Time</strong>: <em>&lt;current-time&gt;</em>
        </li>
        <li>
          A drawing panel which will contain the graphical representation of the simulation with:
          <ul>
            <li>
              A representation of the statue
            </li>
            <li>
              A representation of the cat and mouse via graphic objects. You have full creative control
              over what these graphic objects look like (you could draw an actual cat and mouse, if you
              wish, or Pac-man arcs, or ...). However, the graphic you choose to represent your cat and
              mouse must have two properties (notice the arrowheads we use below have these):
              <ul>
                <li>
                  It must be evident which direction it is facing
                </li>
                <li>
                  It must be evident what the (x,y) point is that you're simulating (it's the tip of our arrowheads).
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>

      <p>Furthermore, the simulation should support two additional command-line options (in addition to the
        three you supported for the original project):</p>

      <ul>
        <li>
          <strong>pixelsPerMeter</strong> - This determines what the scale of your simulation is (the radius
          of the statue is 1 meter, for reference). Given this and the <code>catRadius</code>, you should
          create a drawing panel that fits all of your elements comfortably on the screen at time = 0.
        </li>
        <li>
          <strong>millisecondsPerSimStep</strong> - This determines how many milliseconds of actual time pass
          between simulation time steps, which are "minutes" here.
        </li>
      </ul>

      <p>The text output of the program should be roughly the same as in project 3, so you have a transcript
        of the simulation output.</p>
    </article>

    <article>
      <h2>Example GUI</h2>
      <figure>
        <img class="img-responsive center-block" src="./images/cat-and-mouse-gui.png" alt="Cat and Mouse figure" width="329">
        <figcaption>Figure 2. Example Cat and Mouse GUI simulation. The yellow cat is chasing the blue mouse,
          and there is an added feature to this simulation -- a line from the cat's point to the mouse's point
          (the tips of the arrowheads). When this line intersects the statue, it means the cat cannot see the
          mouse. The simulation has simply been stepped forward one minute, and is "stopped".</figcaption>
      </figure>
    </article>

    <article>
      <h2>Example command-line interaction</h2>

      <p>
        Example Cat and Mouse GUI command-line launch. On the second invocation, the user simply clicked the
        <strong>run</strong> button, then the <strong>quit</strong> button.
      </p>

      <pre v-highlightjs><code class="plain">$ <b>java CatMouseSimulationGUI</b>
Usage: java CatMouseSimulationGUI &lt;pixelsPerMeter&gt; &lt;millisecondsPerSimStep&gt; &lt;catRadius&gt; &lt;catAngle&gt; &lt;mouseAngle&gt;
$ <b>java CatMouseSimulationGUI 100 1000 3 0 0</b>
Welcome to the CS9G cat and mouse GUI simulation!
TIME    STATUS  MOUSE       CAT
0       init    (1.0,0.00)  (3.0,0.00)
1       running (1.0,1.00)  (2.0,0.00)
2       running (1.0,2.00)  (1.0,0.00)
3       eaten   (1.0,2.00)  (1.0,1.25)</code></pre>

    </article>

    <article>
      <h2>Suggested approach</h2>
      <ul>
        <li>
          Copy your project 3 code to a new directory so you don't touch your working project 3 code.
        </li>
        <li>
          Take a look at the your classes from project 3 and decide what features you need to add to your
          <code>Cat</code> and <code>Mouse</code> classes.
        </li>
      </ul>
    </article>

    <article>
      <h2>Extra for Experts (Optional)</h2>
      <p>We've opened Pandora's box by providing a really nice "Artificial Life" simulation environment. Here are some things you could do to have some fun:</p>
      <ul>
        <li>Rather than have the simulation step through your animation as it currently does: simulation-step (long pause) simulation step (long pause) etc, why not have the GUI smoothly animate the cat and mouse <em>between</em> simulation steps? The cat could become bold (or change color in some way) to indicate it hat hit a simulation timestep and was deciding what to do.</li><li>Add a slider that lets you "scrub" forward and backward in time.

      </li><li>Learn about threads and add a 'stop' button that will stop the simulation when it's running.
      </li><li>...and the three from the original Cat and Mouse project:
        <ul>
          <li>Add a <code>Dog</code> class which chases the <code>Cat</code> (and a <code>Bear</code> that chases the <code>Dog</code>, and a <code>Hunter</code> who chases the <code>Bear</code>, etc.) all with different movement rules
          </li><li>Add multiple mice (and multiple statues) and have the Cat chase whichever it sees and is closer to
        </li><li>See what happens if the cat moves continuously rather than making discrete steps every minute. The instant the mouse peeks in from around the statue, the cat should switch from rotating to moving inwards.
        </li></ul>

      </li></ul>
    </article>

    <article>
      <h2>Checklist</h2>
      <ul>
        <li>Correctly working code
        </li><li>Sufficient testing, with output sufficient to verify test correctness:
        <ul>
          <li>tests on specified values for cat and mouse positions;
          </li><li>evidence of independent tests of all methods you authored in each class
        </li><li>tests sufficient to exercise all statements in the program.
        </li></ul>
      </li><li>Good style
        <ul>
          <li>appropriate use of indenting and white space;
          </li><li>variable and method names that reflect their use;
        </li><li>informative comments at the head of each method;
        </li></ul>

      </li>
      </ul>
    </article>

  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/java/CatAndMouseGui.vue